<template>
  <div class="promotions-index">
    <!-- Header -->
    <div class="page-header">
      <h2>مدیریت جشنواره‌ها و هدایا</h2>
      <button class="btn-primary" @click="goToCreate">
        + جشنواره جدید
      </button>
    </div>

    <!-- Filters -->
    <div class="filter-card">
      <div class="filter-row">
        <div class="filter-group">
          <label>نوع:</label>
          <select v-model="filters.type" @change="loadPromotions">
            <option value="">همه</option>
            <option value="first_visit">بار اول</option>
            <option value="loyalty_count">وفاداری</option>
          </select>
        </div>

        <div class="filter-group">
          <label>وضعیت:</label>
          <select v-model="filters.is_active" @change="loadPromotions">
            <option value="">همه</option>
            <option value="1">فعال</option>
            <option value="0">غیرفعال</option>
          </select>
        </div>

        <button class="btn-secondary" @click="resetFilters">پاک کردن</button>
      </div>
    </div>

    <!-- Table -->
    <div class="table-card">
      <div v-if="loading" class="loading-text">در حال بارگذاری...</div>

      <div v-else-if="promotions.length === 0" class="empty-state">
        <div class="empty-icon">📭</div>
        <h3>هیچ جشنواره‌ای یافت نشد</h3>
        <p>اولین جشنواره خود را ایجاد کنید.</p>
        <button class="btn-primary" @click="goToCreate">ایجاد جشنواره</button>
      </div>

      <div v-else class="cards-grid">
        <div
            v-for="row in promotions"
            :key="row.id"
            class="card-item"
            :class="{ inactive: !row.is_active }"
        >
          <!-- ===== هدر کارت ===== -->
          <div class="card-header">
            <div class="card-title-group">
              <h3 class="card-title">{{ row.title }}</h3>
              <span :class="['badge', row.is_active ? 'badge-green' : 'badge-red']">
              {{ row.is_active ? 'فعال' : 'غیرفعال' }}
            </span>
            </div>
            <div class="card-type">
            <span :class="['badge', row.type === 'first_visit' ? 'badge-blue' : 'badge-orange']">
              {{ row.type === 'first_visit' ? 'بار اول' : 'وفاداری' }}
            </span>
            </div>
          </div>

          <!-- ===== بدنه کارت ===== -->
          <div class="card-body">
            <div class="card-row">
              <span class="card-label">خدمت:</span>
              <span class="card-value">{{ row.service ? row.service.title : 'همه خدمات' }}</span>
            </div>

            <div class="card-row">
              <span class="card-label">جایزه:</span>
              <span class="card-value reward-value">
              <span v-if="row.reward_type === 'percentage'">
                {{ row.reward_value }}% تخفیف
                <span v-if="row.max_discount_amount" class="reward-max">
                  (سقف {{ formatPrice(row.max_discount_amount) }})
                </span>
              </span>
              <span v-else-if="row.reward_type === 'fixed_amount'">
                {{ formatPrice(row.reward_value) }} تخفیف
              </span>
              <span v-else-if="row.reward_type === 'free_service'">
                🎁 خدمت رایگان
              </span>
            </span>
            </div>

            <div class="card-row" v-if="row.type === 'loyalty_count'">
              <span class="card-label">شرایط:</span>
              <span class="card-value">{{ row.min_visits }} بار در {{ row.within_days }} روز</span>
            </div>

            <div class="card-row">
              <span class="card-label">تاریخ:</span>
              <span class="card-value">{{ formatDate(row.start_date) }} → {{ formatDate(row.end_date) }}</span>
            </div>

            <div class="card-row" v-if="row.created_by_name">
              <span class="card-label">ساخته شده توسط:</span>
              <span class="card-value">{{ row.created_by_name }}</span>
            </div>
          </div>

          <!-- ===== فوتر کارت (عملیات) ===== -->
          <div class="card-footer">
            <button class="btn-action btn-edit" @click="goToEdit(row.id)">
              ✏️ ویرایش
            </button>
            <button class="btn-action btn-report" @click="goToReport(row.id)">
              📊 گزارش
            </button>
            <button
                class="btn-action btn-delete"
                @click="deletePromotion(row)"
                :disabled="row.has_used_promotions"
            >
              🗑️ حذف
            </button>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <PPagination
          v-if="pagination.total > 0"
          :current-page="pagination.current_page"
          :total-pages="pagination.last_page"
          :total="pagination.total"
          @change-page="handlePageChange"
      />
    </div>

    <!-- مدیریت کوپن‌های کاربران -->
    <div class="user-promotions-section">
      <h3>مدیریت کوپن‌های کاربران</h3>
      <UserPromotions />
    </div>
  </div>
</template>

<script>
import promotionApi from '../../api/promotion';
import UserPromotions from './UserPromotions.vue';
import PPagination from './PPagination.vue';

export default {
  name: 'PromotionsIndex',

  components: {
    UserPromotions,
    PPagination
  },

  data() {
    return {
      loading: false,
      promotions: [],
      filters: {
        type: '',
        is_active: null
      },
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
      }
    };
  },

  mounted() {
    this.loadPromotions();
  },

  methods: {
    async loadPromotions() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.current_page,
          per_page: this.pagination.per_page,
          type: this.filters.type || undefined,
          is_active: this.filters.is_active !== '' ? this.filters.is_active : undefined
        };

        const { data } = await promotionApi.getPromotions(params);
        this.promotions = data.data || [];
        this.pagination = {
          current_page: data.current_page || 1,
          last_page: data.last_page || 1,
          per_page: data.per_page || 15,
          total: data.total || 0
        };
      } catch (error) {
        alert('خطا در بارگذاری اطلاعات');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    handlePageChange(page) {
      if (page < 1 || page > this.pagination.last_page) return;
      this.pagination.current_page = page;
      this.loadPromotions();
    },

    goToCreate() {
      this.$router.push('/dashboard/promotions/create');
    },

    goToEdit(id) {
      this.$router.push(`/dashboard/promotions/edit/${id}`);
    },

    goToReport(id) {
      this.$router.push(`/dashboard/promotions/report/${id}`);
    },

    async deletePromotion(row) {
      if (!confirm(`آیا از حذف جشنواره "${row.title}" اطمینان دارید؟`)) return;

      try {
        await promotionApi.deletePromotion(row.id);
        alert('جشنواره با موفقیت حذف شد');
        this.loadPromotions();
      } catch (error) {
        alert(error.response?.data?.message || 'خطا در حذف');
      }
    },

    resetFilters() {
      this.filters = { type: '', is_active: null };
      this.pagination.current_page = 1;
      this.loadPromotions();
    },

    formatPrice(value) {
      return new Intl.NumberFormat('fa-IR').format(value) + ' تومان';
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('fa-IR');
    }
  }
};
</script>

<style scoped>
/* ===== استایل‌های عمومی ===== */
.promotions-index {
  padding: 20px;
}

/* ===== Header ===== */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #2c3e50;
}

/* ===== دکمه‌ها ===== */
.btn-primary {
  padding: 10px 20px;
  background: #409EFF;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;
}

.btn-primary:hover {
  background: #66b1ff;
}

.btn-secondary {
  padding: 8px 16px;
  background: #909399;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-secondary:hover {
  background: #a6a9ad;
}

.btn-edit, .btn-report, .btn-delete {
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: opacity 0.2s;
}

.btn-edit {
  background: #409EFF;
  color: white;
}

.btn-report {
  background: #67C23A;
  color: white;
}

.btn-delete {
  background: #F56C6C;
  color: white;
}

.btn-edit:hover, .btn-report:hover, .btn-delete:hover {
  opacity: 0.8;
}

.btn-delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-group {
  display: flex;
  gap: 5px;
}

/* ===== فیلتر ===== */
.filter-card {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 15px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-group label {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
}

.filter-group select {
  padding: 6px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  min-width: 120px;
}

/* ===== جدول ===== */
.table-card {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow-x: auto;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.table th {
  background: #f5f7fa;
  color: #606266;
  font-weight: 600;
  padding: 12px 8px;
  text-align: right;
  border-bottom: 2px solid #e4e7ed;
}

.table td {
  padding: 12px 8px;
  border-bottom: 1px solid #ebeef5;
  color: #303133;
}

.table tr:hover td {
  background: #f5f7fa;
}

.text-center {
  text-align: center;
  padding: 30px !important;
  color: #909399;
}

.loading-text {
  text-align: center;
  padding: 40px;
  color: #909399;
}

/* ===== بج‌ها ===== */
.badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.badge-success {
  background: #f0f9eb;
  color: #67C23A;
}

.badge-warning {
  background: #fdf6ec;
  color: #E6A23C;
}

.badge-danger {
  background: #fef0f0;
  color: #F56C6C;
}

/* ===== بخش کوپن‌ها ===== */
.user-promotions-section {
  margin-top: 30px;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.user-promotions-section h3 {
  margin-top: 0;
  color: #2c3e50;
}
.promotions-manager {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  direction: rtl;
}

/* ========================================== */
/* هدر */
/* ========================================== */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
  color: #2c3e50;
  font-size: 24px;
}

.page-header .subtitle {
  margin: 4px 0 0 0;
  color: #909399;
  font-size: 14px;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s;
  white-space: nowrap;
}

.btn-primary:hover {
  background: #66b1ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(64, 158, 255, 0.35);
}

/* ========================================== */
/* فیلترها */
/* ========================================== */
.filter-section {
  background: white;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-group label {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
}

.filter-group select {
  padding: 6px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 13px;
  background: white;
  min-width: 120px;
}

.btn-reset {
  padding: 6px 16px;
  background: #f5f7fa;
  color: #606266;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
}

.btn-reset:hover {
  background: #e4e7ed;
}

/* ========================================== */
/* حالت بارگذاری و خالی */
/* ========================================== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  color: #909399;
  gap: 12px;
  background: white;
  border-radius: 12px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #409eff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.empty-state h3 {
  color: #2c3e50;
  margin-bottom: 8px;
}

.empty-state p {
  color: #909399;
  margin-bottom: 20px;
}

/* ========================================== */
/* گرید کارت‌ها */
/* ========================================== */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

/* ========================================== */
/* کارت جشنواره */
/* ========================================== */
.card-item {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s;
  border-right: 4px solid #409eff;
}

.card-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.card-item.inactive {
  border-right-color: #c0c4cc;
  opacity: 0.7;
}

/* ===== هدر کارت ===== */
.card-header {
  padding: 16px 20px 12px 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px;
}

.card-title-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
}

.card-type {
  display: flex;
  align-items: center;
}

/* ===== بج‌ها ===== */
.badge {
  display: inline-block;
  padding: 2px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.badge-green {
  background: #f0f9eb;
  color: #67c23a;
}

.badge-red {
  background: #fef0f0;
  color: #f56c6c;
}

.badge-blue {
  background: #ecf5ff;
  color: #409eff;
}

.badge-orange {
  background: #fdf6ec;
  color: #e6a23c;
}

.badge-gray {
  background: #f4f4f5;
  color: #909399;
}

/* ===== بدنه کارت ===== */
.card-body {
  padding: 14px 20px;
}

.card-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 4px 0;
  font-size: 14px;
  color: #303133;
  flex-wrap: wrap;
}

.card-label {
  font-weight: 500;
  color: #909399;
  min-width: 60px;
}

.card-value {
  color: #303133;
}

.reward-value {
  font-weight: 500;
  color: #409eff;
}

.reward-max {
  font-size: 12px;
  color: #909399;
  font-weight: normal;
}

/* ===== فوتر کارت (عملیات) ===== */
.card-footer {
  padding: 12px 20px 16px 20px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.btn-action {
  padding: 6px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
  flex: 1;
  min-width: 60px;
  text-align: center;
}

.btn-action:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-edit {
  background: #ecf5ff;
  color: #409eff;
}
.btn-edit:hover:not(:disabled) {
  background: #409eff;
  color: white;
}

.btn-report {
  background: #f0f9eb;
  color: #67c23a;
}
.btn-report:hover:not(:disabled) {
  background: #67c23a;
  color: white;
}

.btn-delete {
  background: #fef0f0;
  color: #f56c6c;
}
.btn-delete:hover:not(:disabled) {
  background: #f56c6c;
  color: white;
}

/* ========================================== */
/* بخش کوپن‌های کاربران */
/* ========================================== */
.user-promotions-section {
  margin-top: 40px;
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.user-promotions-section h3 {
  margin: 0 0 16px 0;
  color: #2c3e50;
  font-size: 18px;
}

/* ========================================== */
/* ریسپانسیو */
/* ========================================== */
@media (max-width: 1024px) {
  .cards-grid {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
}

@media (max-width: 768px) {
  .promotions-manager {
    padding: 12px;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .btn-primary {
    justify-content: center;
  }

  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-group {
    flex-wrap: wrap;
  }

  .filter-group select {
    flex: 1;
    min-width: 80px;
  }

  .btn-reset {
    width: 100%;
    justify-content: center;
  }

  .cards-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .card-header {
    flex-direction: column;
    align-items: stretch;
  }

  .card-footer {
    flex-direction: column;
  }

  .btn-action {
    flex: none;
    width: 100%;
  }

  .user-promotions-section {
    padding: 12px;
  }
}

@media (max-width: 480px) {
  .promotions-manager {
    padding: 8px;
  }

  .page-header h2 {
    font-size: 20px;
  }

  .card-title {
    font-size: 15px;
  }

  .card-body {
    padding: 12px 16px;
  }

  .card-row {
    font-size: 13px;
  }
}
</style>